/* eslint-disable */
// istanbul ignore file
import { CircularProgress, Modal } from "@mui/material";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import { SnackbarOrigin } from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { styled } from "@mui/material/styles";
import { visuallyHidden } from "@mui/utils";
import * as React from "react";
import { useRef } from "react";
import { connect, useSelector, type ConnectedProps } from "react-redux";
import AuthError from "../../../../components/ErrorComponents/AuthError";
import GenericModal from "../../../../components/Modal";
import Pagination from "../../../../components/TablePagination";
import LanguageStrings from "../../../../i18n/locales";
import { type RootState } from "../../../../store";
import ViewAsset from "../ViewAsset/ViewAsset";
import { assetDeleteCampaign, fetchAssetList } from "../../redux/actionCreator";
import { AssetListState } from "../../redux/assetListSlice";
import { APIConfiguration } from "../../../../utils/apiConfiguration";
import { useNavigate } from "react-router-dom";
import { getOperationPermissions } from '../../../dashboard/redux/actionCreator'
import { type OperationPermissionState } from '../../../dashboard/redux/operationPermissionSlice'
import { useAuth } from 'react-oidc-context'
import useRefreshHamburgNotifier from '../../../../customHooks/useRefreshHamburgNotifier'

const AssetManagementStrings = LanguageStrings().AssetManagementCollectionStrings.TableComponent;
const httpStatus = APIConfiguration;

export interface State extends SnackbarOrigin {
  open: boolean;
}

interface Data {
  name: string;
  description: string;
}
export type Order = "asc" | "desc";

interface HeadCell {
  disablePadding: boolean;
  id: any;
  label: string;
  numeric: boolean;
  sortable: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "name",
    numeric: false,
    disablePadding: false,
    label: AssetManagementStrings.AssetName,
    sortable: true,
  },
  {
    id: "description",
    numeric: false,
    disablePadding: false,
    label: AssetManagementStrings.Description,
    sortable: false,
  },
  {
    id: "action",
    numeric: false,
    disablePadding: false,
    label: AssetManagementStrings.Action,
    sortable: false,
  },
];

function EnhancedTableHead(props: any): any {
  const { order, orderBy, onRequestSort, assetList } = props;
  const createSortHandler =
    (newOrderBy: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, newOrderBy);
    };

  // istanbul ignore else
  return (
    <TableHead data-testid="table-header">
      <TableRow>
        {headCells.map((headCell: any) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              sx={{
                "& .MuiTableSortLabel-icon": {
                  opacity: 1,
                  marginLeft: {
                    xs: '100px',
                    xl: '215px'
                  }
                },
                minWidth: '200px'
              }}
              hideSortIcon={!headCell.sortable}
              direction={orderBy === headCell.id ? order : "desc"}
              onClick={
                headCell.sortable ? createSortHandler(headCell.id) : undefined
              }
              aria-label={headCell.label}
              data-testid="test-sort"
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#F0F0F0",
    fontWeight: "700",
    fontSize: 12,
    border: "2px solid #202020",
    padding: "5px 16px",
    fontFamily: "Honeywell Sans Web",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "#d0d0d0",
    paddingLeft: "17px",
    borderBottom: "1px solid #202020",
    borderRight: 0,
    fontFamily: "Honeywell Sans Web",
  },
}));

export interface Sort {
  order: Order;
  orderBy: string;
}

function AssetList(props: AllProps) {
  const navigate = useNavigate();
  const [confirmDeletePopup, setConfirmDeletePopup] = React.useState(false);
  const [selectedAssetId, setSelectedAssetId] = React.useState("");
  const [selectedAssetName, setSelectedAssetName] = React.useState("");
  const [showDeleteStatus, setShowDeleteStatus] = React.useState(false);
  const operationPermissions: OperationPermissionState = useSelector((state: RootState) => state.operationPermissions)
  const auth = useAuth()
  const { refreshHamburgAction } = useRefreshHamburgNotifier()

  React.useEffect(() => {
    (!operationPermissions.updated && props.getOperationPermissions(auth.user?.profile.sub as string))
  }, [])

  const assetDeleteStatus = useSelector(
    (state: RootState) => state.assetDeleteState
  );
  // const [getAssetTitle, setAssetTitle] = React.useState("");
  // const [getAssetTypeId, setAssetTypeId] = React.useState("");
  const [open, setOpen] = React.useState<any>("");

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);

  // istanbul ignore next
  function useOutsideAlerter(ref: any) {}

  React.useEffect(() => {
    if (props.searchVal === "") {
      fetchAllAssetList();
    } else if (props.searchVal !== "") {
      props.performAction();
    }
  }, [props.page, props.sort]);

  const fetchAllAssetList = () => {
    // istanbul ignore else
    if (props.page >= 1) {
      props.fetchAssetList(props.siteId, props.assetTypeId, props.page, props.rowsPerPage, props.searchVal, props.sort.order, props.parentAssetId, props.sort.orderBy);
    }
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    newOrderBy: keyof Data
  ) => {
    const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === "asc";
    const toggledOrder = isAsc ? "desc" : "asc";
    props.setSort({ order: toggledOrder, orderBy: newOrderBy });
  };

  const handleAssetDetailClick = (assetId: string, assetName: string) => {
    navigate(`/gateway-list/${assetId}`, { state: { parentSiteId: props.parentAssetId } });
  };

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "& th": {
      border: "1px solid #202020",
    },
  }));

  const ViewAssetOpen = (id: any): any => {
    setOpen(id);
  };

  const handleDeleteCampaignWithFailure = (): JSX.Element => {
    // istanbul ignore else
    if (assetDeleteStatus.httpStatus === httpStatus.SUCCESS_HTTP_STATUS) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={AssetManagementStrings.successfullyDeleted}
          LabelPositive="Okay"
          onClickPositive={() => {
            refreshHamburgAction('Asset', 'Delete'), setShowDeleteStatus(false), fetchAllAssetList();
          }}
          onClose={() => {
            refreshHamburgAction('Asset', 'Delete'), setShowDeleteStatus(false), fetchAllAssetList();
          }}
        >
          <p style={{ paddingTop: "1.2em" }}>
            {" "}
            {AssetManagementStrings.FullMsgAssetDeletedSuccessfully}{" "}
          </p>
        </GenericModal>
      );
    } else if (assetDeleteStatus.isLoading) {
      return (
        <GenericModal title={"Loading..."}>
          <div className="CircularprogressAmi">
            <CircularProgress />
          </div>
        </GenericModal>
      );
    } else if (assetDeleteStatus.httpStatus === httpStatus.BAD_REQUEST) {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {assetDeleteStatus.error} </p>
        </GenericModal>
      );
    } else if (
      assetDeleteStatus.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS
    ) {
      return (
        <div className="authError">
          <AuthError errorMessage="Unauthorized" />
        </div>
      );
    } else if (
      assetDeleteStatus.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS
    ) {
      return (
        <div className="authError">
          <AuthError errorMessage="accessForbidden" />
        </div>
      );
    } else {
      return (
        <GenericModal
          buttonTypeNegative="hollow"
          title={"Failed"}
          LabelPositive="Okay"
          onClickPositive={() => {
            setShowDeleteStatus(false);
          }}
          onClose={() => {
            setShowDeleteStatus(false);
          }}
        >
          <p style={{ paddingTop: "1.2em" }}> {assetDeleteStatus.error} </p>
        </GenericModal>
      );
    }
  };

  // istanbul ignore else
  if (
    props.assetList.assetList !== null &&
    props.assetList.assetList !== undefined &&
    props.assetList.httpStatus === httpStatus.SUCCESS_HTTP_STATUS &&
    props.assetList.assetCount > 0
  ) {
    return (
      <>
        <Box className="assets">
          <Paper sx={{ width: "100%", mb: 2, backgroundColor: "#272727", boxShadow: "unset", color: "#F0F0F0", marginBottom: "0" }}>
            <TableContainer className="asset-table">
              <Table sx={{ color: "#f0f0f0" }} aria-labelledby="tableTitle" size={"small"}>
                <EnhancedTableHead
                  order={props.sort.order}
                  orderBy={props.sort.orderBy}
                  onRequestSort={handleRequestSort}
                  rowCount={props.assetList.assetList.length}
                  assetList={props.assetList.assetList}
                  assetListCount={props.assetList.assetList.length}
                />
                <TableBody>
                  {props.assetList.assetList &&
                    props.assetList.assetList.map((row: any, index: any) => {
                      const labelId = `enhanced-table-checkbox-${index}`;
                      return (
                        <StyledTableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row.assetId}
                          aria-label="assets-list-row"
                          sx={{ cursor: "pointer" }}
                        >
                          <StyledTableCell
                            component="th"
                            id={labelId}
                            scope="row"
                            padding="none"
                            onClick={() =>
                              handleAssetDetailClick(row.id, row.name)
                            }
                            aria-label="asset-name"
                            data-testid="asset-name"
                            sx={{ color: "#64C3FF !important" }}
                          >
                            {row.name}
                          </StyledTableCell>
                          <StyledTableCell>{row.description}</StyledTableCell>
                          <StyledTableCell style={{ width: "220px" }}>
                            {operationPermissions.permissions.can_read_tiq_assets && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px" }}
                                onClick={() => ViewAssetOpen(row.id)}
                                data-testid="change-state-button"
                              >
                                {AssetManagementStrings.View}
                              </span>
                            )}
                            {operationPermissions.permissions.can_modify_tiq_assets && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px", marginRight: "15px" }}
                                onClick={() => {
                                  navigate(`/edit-asset/${row.id}`, { state: {
                                    assetId: row.id, gatewayId: row.gateway_id, gatewayName: row.gateway_name, assetName: row.name,
                                    assetDescription: row.description, parentAssetId: props.parentAssetId
                                  } })
                                }}
                                data-testid="change-state-button"
                              >
                                {AssetManagementStrings.Edit}
                              </span>
                            )}
                            {operationPermissions.permissions.can_delete_tiq_assets && (
                              <span style={{ color: "#64c3ff", textDecoration: "none", paddingLeft: "2px" }}
                                onClick={() => {
                                  setSelectedAssetId(row.id);
                                  setSelectedAssetName(row.name);
                                  setConfirmDeletePopup(true);
                                }}
                                data-testid="change-state-button"
                              >
                                {AssetManagementStrings.Delete}
                              </span>
                            )}
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={props.assetList.assetCount}
              page={props.page}
              setPage={props.setPage}
              rowsPerPage={props.rowsPerPage}
              siblingCount={1}
              boundaryCount={1}
            />
          </Paper>
        </Box>
        {open !== "" && (
          <>
              <ViewAsset
                viewAssetPopup={(params?: any) => {
                  setOpen(params);
                }}
                data-testid="viewasset_chk"
                assetId={open}
              />
          </>
        )}
        {confirmDeletePopup && (
          <GenericModal
            buttonTypeNegative="hollow"
            title={`${AssetManagementStrings.Delete} ${selectedAssetName}`}
            LabelPositive="Confirm"
            LabelNegative="Cancel"
            onClickPositive={() => {
              setConfirmDeletePopup(false);
              setShowDeleteStatus(true);
              props.assetDeleteCampaign(selectedAssetId, auth.user?.profile.sub ?? '');
            }}
            onClose={() => {
              setConfirmDeletePopup(false);
            }}
            onClickNegative={() => {
              setConfirmDeletePopup(false);
            }}
          >
            <p style={{ paddingTop: "1.2em", fontSize: "12px" }}>
              {AssetManagementStrings.DeleteConfirmation}
              <br />
              {AssetManagementStrings.DeleteConfirmationWarning}
            </p>
          </GenericModal>
        )}
        {showDeleteStatus && handleDeleteCampaignWithFailure()}
      </>
    );
  } else if (props.assetList.isLoading) {
    return (
      <div className="CircularprogressAmi" data-testid="loading-spinner">
        <CircularProgress />
      </div>
    );
  } else if (props.assetList.httpStatus === httpStatus.UNAUTHORIZED_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="Unauthorized" />
      </div>
    );
  } else if (props.assetList.httpStatus === httpStatus.ACCESS_FORBIDDEN_HTTP_STATUS) {
    return (
      <div className="authError">
        <AuthError errorMessage="accessForbidden" />
      </div>
    );
  } else if (
    props.assetList.assetList !== null &&
    props.assetList.assetList !== undefined &&
    props.assetList.assetList.length === 0
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else if (
    props.assetList.loadingError?.includes(AssetManagementStrings.NoAssetsFound) ||
    props.assetList.loadingError?.includes(AssetManagementStrings.AssetNotFound)
  ) {
    return (
      <div className="authError">
        <AuthError errorMessage="NoDataPresent" />
      </div>
    );
  } else {
    return (
      <div className="authError">
        <AuthError errorMessage="cannotFetch" retry={fetchAllAssetList} />
      </div>
    );
  }
}

interface DispatchToProps {
  fetchAssetList: (
    siteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) => void;
  assetDeleteCampaign: (selectedAssetId: string, subId: string) => void;
  getOperationPermissions: (sub: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchAssetList: (
    siteId: string,
    assetTypeId: string,
    PageNumber: number,
    PageItems: number,
    searchKey: string,
    SortParams: string,
    parentAssetId: string,
    sortField: string
  ) =>
    dispatch(
      fetchAssetList(
        siteId,
        assetTypeId,
        PageNumber,
        PageItems,
        searchKey,
        SortParams,
        parentAssetId,
        sortField
      )
    ),
    assetDeleteCampaign: (selectedAssetId: string, subId: string) => dispatch(assetDeleteCampaign(selectedAssetId, subId)),
    getOperationPermissions: (sub: string) => dispatch(getOperationPermissions(sub))
});

interface StateToProps {
  assetList: AssetListState;
}
const mapStateToProps = (state: RootState): StateToProps => ({
  assetList: state.assetManagementAssetList,
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface ParentProps {
  siteId: string;
  assetTypeId: string;
  searchVal: string;
  parentAssetId: string;
  performAction: () => void;
  rowsPerPage: number;
  page: number;
  sort: Sort;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setSort: React.Dispatch<React.SetStateAction<Sort>>;
}

type AllProps = PropsFromRedux & ParentProps;

export default connector(AssetList);
